import { DAYS_IN_WEEK, SUBSCRIPTION_FREQUENCY, SUBSCRIPTION_TYPE } from '@urc-ui/utils/types';
import { SubscriptionDelivery } from '@urc-ui/store/api/subscriptions/types';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { isAfter } from 'date-fns';
import dateTimeFns from '@urc-ui/components/date-input/utils/date-time-fns';
import { ReportPeriodMaxLookBack } from '@urc-ui/store/api/report-metadata/types';

/**
 * Calculates the maximum expiration date for recurring subscriptions with CUSTOM date ranges.
 * Returns undefined if no limit should be applied.
 *
 * @param reportStartDate the start date of the CUSTOM report period
 * @param lookback the lookback configuration from report metadata
 */
export const getMaxExpirationDate = (
  reportStartDate: string | null,
  lookback: ReportPeriodMaxLookBack | null | undefined
): Date | undefined => {
  if (!reportStartDate || !lookback) {
    return undefined;
  }

  if (lookback.maxLookBackDays && lookback.maxLookBackDays > 0) {
    const startDate = new Date(reportStartDate);
    return dateTimeFns.addDays(lookback.maxLookBackDays - 1)(startDate);
  }

  if (lookback.maxLookBackDate) {
    return new Date(lookback.maxLookBackDate);
  }

  return undefined;
};

/**
 * Returns SubscriptionDelivery object for the one-off subscription.
 * The one-off reports are immediately scheduled.
 */
export const getNowSubscriptionDelivery = () => {
  return {
    frequency: SUBSCRIPTION_FREQUENCY.SINGLE
  };
};

/**
 * Returns SubscriptionDelivery object for the subscription scheduled to run in the future.
 * Frequency is SINGLE for both one-off and Future subscription. But, unlike one-off subscription,
 * future SubscriptionDelivery will include scheduledEndDate, hour, minute, deliveryDate, deliveryMonth and deliveryYear.
 * These fields are returned in the marketplace timezone
 *
 * @param futureDate date based on which the SubscriptionDelivery object is built.
 * @param timezone optional marketplace timezone. If provided, the futureDate input is assumed to be in UTC and will be converted
 * @return SubscriptionDelivery future subscription in marketplace timezone
 */
export const getFutureSubscriptionDelivery = (futureDate: Date, timezone?: string) => {
  const date = timezone ? utcToZonedTime(futureDate, timezone) : futureDate;

  return {
    frequency: SUBSCRIPTION_FREQUENCY.SINGLE,
    scheduledEndDate: date.getTime(),
    deliveryDate: date.getDate(),
    deliveryMonth: dateTimeFns.getMonthName(date.getMonth()),
    deliveryYear: date.getFullYear(),
    hour: date.getHours(),
    minute: date.getMinutes()
  };
};

/**
 * Returns a date (in utc) given a future subscription delivery object
 * @param subscriptionDelivery the subscription deliver object
 * @param timezone the marketplace timezone
 */
export const getDateFromFutureSubscriptionDelivery = (subscriptionDelivery: SubscriptionDelivery, timezone: string) => {
  if (!subscriptionDelivery.deliveryDate || !subscriptionDelivery.deliveryMonth || !subscriptionDelivery.deliveryYear) {
    return undefined;
  }

  const year = `${subscriptionDelivery.deliveryYear}`.padStart(4, '0');
  const month = dateTimeFns.getMonthNumber(subscriptionDelivery.deliveryMonth);
  const date = `${subscriptionDelivery.deliveryDate}`.padStart(2, '0');
  const hour = subscriptionDelivery.hour ? `${subscriptionDelivery.hour}`.padStart(2, '0') : null;
  const minute = subscriptionDelivery.minute ? `${subscriptionDelivery.minute}`.padStart(2, '0') : null;

  const dateString = hour && minute ? `${year}-${month}-${date}T${hour}:${minute}` : `${year}-${month}-${date}`;
  return zonedTimeToUtc(dateString, timezone);
};

/**
 * Returns default SubscriptionDelivery object for the recurring subscription.
 *
 * @param scheduleEndDate date until the scheduled recurring subscription expires. In marketplace timezone
 * @return SubscriptionDelivery daily subscription delivery in marketplace timezone
 */
export const getDefaultRecurringSubscriptionDelivery = (scheduleEndDate: Date) => {
  return {
    frequency: SUBSCRIPTION_FREQUENCY.DAILY,
    scheduledEndDate: scheduleEndDate.getTime()
  };
};

/**
 * Returns Subscription type based on the input delivery object.
 * Non-SINGLE frequency subscriptions are interpreted as recurring subscriptions.
 * SINGLE frequency subscriptions with scheduledEndDate are interpreted as future subscriptions, otherwise one-off.
 *
 * @param delivery SubscriptionDelivery object to extract subscriptionType from
 * @param timezone the marketplace timezone. The subscription delivery input is expected to be in the marketplace timezone
 */
export const getSubscriptionTypeFromDelivery = (delivery: SubscriptionDelivery, timezone: string) => {
  if (delivery.frequency !== SUBSCRIPTION_FREQUENCY.SINGLE) {
    return SUBSCRIPTION_TYPE.RECURRING;
  }
  const futureDeliveryDate = getDateFromFutureSubscriptionDelivery(delivery, timezone);
  return futureDeliveryDate && isAfter(futureDeliveryDate, Date.now()) ? SUBSCRIPTION_TYPE.FUTURE : SUBSCRIPTION_TYPE.NOW;
};

/**
 * Returns default SubscriptionDelivery object based on the subscription type.
 * While one-off subscriptions doesn't include any info other than frequency,
 * Future and Recurring subscriptionDeliveries include schedule info calculated as below,
 *
 * Future: Delivery date is set to tomorrow i.e., currentDate + 1.
 *
 * Recurring: Delivery date is set to 1st day of next month.
 *
 * @param subscriptionsType subscription type based on which delivery object is built
 * @param timezone the marketplace timezone
 * @return SubscriptionDelivery with dates in the marketplace timezone
 */
export const getSubscriptionDeliveryFromSubscriptionType = (
  subscriptionsType: SUBSCRIPTION_TYPE,
  timezone: string
): SubscriptionDelivery => {
  if (subscriptionsType === SUBSCRIPTION_TYPE.NOW) {
    return getNowSubscriptionDelivery();
  } else if (subscriptionsType === SUBSCRIPTION_TYPE.FUTURE) {
    const tomorrow = dateTimeFns.composeInTZ({ zone: timezone }, dateTimeFns.addDays(1));
    return getFutureSubscriptionDelivery(tomorrow);
  } else if (subscriptionsType === SUBSCRIPTION_TYPE.RECURRING) {
    const oneMonthFromNow = dateTimeFns.composeInTZ({ zone: timezone }, dateTimeFns.addMonths(1));
    return getDefaultRecurringSubscriptionDelivery(oneMonthFromNow);
  }

  throw Error(`Invalid subscriptionType: ${subscriptionsType}`);
};

/**
 * Returns an Array<DAYS_IN_WEEK> built from input SubscriptionDelivery object.
 * Today's date based on timezone, is returned, in case the input deliveryDays is blank.
 *
 * @param delivery SubscriptionDelivery to extract deliveryDays from
 * @param timeZone Marketplace timezone to get today's date
 */
export const getSelectedDaysFromDelivery = (delivery: SubscriptionDelivery, timeZone: string) => {
  if (delivery.deliveryDays) {
    return delivery.deliveryDays.map(day => DAYS_IN_WEEK[day as DAYS_IN_WEEK]);
  }

  const currentDayOfWeek = utcToZonedTime(new Date(), timeZone).toLocaleString('en-US', { weekday: 'long' }).toUpperCase() as DAYS_IN_WEEK;
  return [currentDayOfWeek];
};

/**
 * Returns recurring SubscriptionDelivery based on the reportSettings input.
 * Delivery for different frequencies is built as below,
 *
 * Daily: Delivery includes scheduledEndDate
 *
 * Weekly: Delivery includes scheduledEndDate along with the deliveryDays
 *
 * Monthly: Delivery includes scheduledEndDate along with deliveryDate set to 1st day in a month.
 *
 * @param frequency frequency on the subscription delivery
 * @param scheduledEndDate end date until the schedule expiry
 * @param selectedDays weekly days to schedule the report delivery
 */
export const getRecurringSubscriptionDeliveryFromReportSettings = (
  frequency: SUBSCRIPTION_FREQUENCY,
  scheduledEndDate: Date,
  selectedDays?: DAYS_IN_WEEK[]
): SubscriptionDelivery => {
  const recurringSubscriptionDelivery = {
    frequency,
    scheduledEndDate: scheduledEndDate.getTime()
  };

  if (frequency === SUBSCRIPTION_FREQUENCY.MONTHLY_DATE) {
    return {
      ...recurringSubscriptionDelivery,
      // Deliver report on 1st day of every month.
      deliveryDate: 1
    };
  }

  if (frequency === SUBSCRIPTION_FREQUENCY.WEEKLY) {
    return {
      ...recurringSubscriptionDelivery,
      deliveryDays: selectedDays ? selectedDays.map(key => DAYS_IN_WEEK[key]) : []
    };
  }
  return recurringSubscriptionDelivery;
};
